/* HP on-site quote-request acknowledgement.
 *
 * Tells a B2B buyer, on HP's own site, that their cart reached HP. The other
 * half of the acknowledgement is Shopify's post-submit page, whose copy lives in
 * locales/*.json under shopify.checkout.thank_you.* - same title, same promise.
 *
 * DELIBERATELY NOT A MODAL.
 *   No backdrop, no `body.overflow = hidden`, no focus trap, no aria-modal, no
 *   showModal(). The buyer has just finished an action; a confirmation must not
 *   become an obstacle. The page behind stays scrollable and clickable. Focus is
 *   moved to the panel once - because the panel is the reason the buyer is on
 *   this page - and then Tab moves freely in and out of it again.
 *
 * FAIL CLOSED. Saying "we have your request" to somebody who did not send one is
 * worse than saying nothing, so every uncertain branch shows nothing:
 *
 *   A. ?hp-quote=received in the URL. Deterministic and linkable. This is the
 *      address to put in the acknowledgement e-mail or to read out on the phone.
 *   B. All THREE of:
 *        - the cart's quote-request button was pressed earlier in this session;
 *        - document.referrer is a same-origin checkout thank-you URL;
 *        - /cart.js reports the cart is now empty.
 *      Miss any one of them - referrer stripped, different URL shape, cart not
 *      empty - and nothing is shown. A missed second chance, never a false one.
 *
 * SHARING THE SCREEN. One guard interval runs for the life of the page. It opens
 * the panel only when nothing outranks it, and stands it down again - without
 * recording a dismissal - if Shopify's consent banner or the launch welcome
 * dialog appears afterwards. Nothing depends on winning a race against another
 * component's timers; see WELCOME_WORST_CASE_MS for why that matters.
 *
 * SELECTOR WARNING - read before editing CONSENT_SELECTOR.
 *   `shopify-pc__banner` is an element **id**, not a class. Shopify emits
 *   <section id="shopify-pc__banner" class="shopify-pc__banner__dialog">.
 *   A previous HP component queried `.shopify-pc__banner` as a class, matched 0
 *   nodes on every load, and therefore opened on top of a live consent banner.
 *   Both spellings are matched below. If you change this list, prove it by
 *   calling `privacyBanner.showBanner()` on the live storefront and checking
 *   that this panel actually stands down.
 */
(function () {
  'use strict';

  var DISMISS_KEY = 'hp:quote-ack:v1';
  var SUBMIT_KEY = 'hp:quote-submit:v1';
  var WELCOME_KEY = 'hp:welcome-popup:v1'; // owned by assets/hp-welcome-popup.js, read only
  var HTML_OPEN_CLASS = 'hp-qack-open'; // suppresses the chat launcher, see the CSS
  var SHOW_DELAY_MS = 350;
  var POLL_MS = 400;
  var CLOSE_ANIM_MS = 240; // must match the CSS transition
  var SUBMIT_MAX_AGE_MS = 12 * 60 * 60 * 1000;

  /* The launch welcome dialog's own schedule, measured on the live storefront:
     it waits NO_BANNER_GRACE 5000 ms for a consent banner that never comes, then
     SHOW_DELAY 500 ms, so on a first visit with consent already recorded it opens
     at about t+5500 ms. This panel used to wait 5000+350 and therefore opened
     ~150 ms BEFORE it - two notices stacked on one screen, which is exactly the
     defect this component was written to avoid. So: if the welcome dialog has
     already been dismissed this session it can never open and there is nothing to
     wait for; otherwise wait past its worst case. The guard below is the backstop
     if either schedule ever changes. */
  var WELCOME_WORST_CASE_MS = 6500;

  // Shopify's native consent UI, matched by id AND by class. See the warning above.
  var CONSENT_SELECTOR = [
    '#shopify-pc__banner',
    '#shopify-pc__prefs',
    '#shopify-pc__prefs__overlay',
    '#shopify-pc__prefs__dialog',
    '.shopify-pc__banner',
    '.shopify-pc__banner__dialog',
    '.shopify-pc__banner__wrapper',
    '.shopify-pc__prefs',
    '.shopify-pc__prefs__dialog',
    '.shopify-pc__prefs__overlay'
  ].join(', ');

  // The cart's quote-request control, in the cart page and the cart drawer.
  var SUBMIT_SELECTOR = '#checkout, button[name="checkout"], .cart__checkout-button';

  var root = document.getElementById('hp-quote-ack');
  if (!root) return;

  var panel = root.querySelector('.hp-qack__panel');
  if (!panel) return;

  var scroller = root.querySelector('[data-hp-qack-scroll]');
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  var isOpen = false;
  var lastFocused = null;
  var guard = null;
  var scrollObserver = null;
  var armedAt = 0;
  var clearSince = 0; // when the screen last became free of consent UI / the welcome dialog
  var userDismissed = false;

  /* ------------------------------------------------------------------ *
   * Storage - every access guarded, private mode throws                 *
   * ------------------------------------------------------------------ */

  function readSession(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function writeSession(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (e) {
      /* private mode, storage disabled - the panel still works, it just forgets */
    }
  }

  function dismissed() {
    return readSession(DISMISS_KEY) === '1';
  }

  /* ------------------------------------------------------------------ *
   * Record that this visitor pressed the cart's quote-request button.   *
   * Delegated, capture phase, so it is recorded even if the cart's own  *
   * handler stops propagation or the button is re-rendered.             *
   * ------------------------------------------------------------------ */

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      if (!target.closest(SUBMIT_SELECTOR)) return;
      writeSession(SUBMIT_KEY, String(Date.now()));
    },
    true
  );

  /* ------------------------------------------------------------------ *
   * Should this panel be shown at all?                                  *
   * ------------------------------------------------------------------ */

  function urlAsksForIt() {
    try {
      var params = new URLSearchParams(window.location.search);
      var value = params.get('hp-quote') || params.get('hp_quote');
      return typeof value === 'string' && value.toLowerCase() === 'received';
    } catch (e) {
      return false;
    }
  }

  // Same-origin checkout thank-you page. Cross-origin referrers arrive as a bare
  // origin under strict-origin-when-cross-origin, so they can never match - which
  // is the intended, fail-closed outcome.
  function cameBackFromThankYou() {
    var ref = document.referrer;
    if (!ref) return false;
    try {
      var url = new URL(ref, window.location.href);
      if (url.origin !== window.location.origin) return false;
      if (url.pathname.indexOf('/checkouts/') === -1) return false;
      return /thank[-_]?you/i.test(url.pathname);
    } catch (e) {
      return false;
    }
  }

  function submittedRecently() {
    var raw = readSession(SUBMIT_KEY);
    if (!raw) return false;
    var t = parseInt(raw, 10);
    if (!t || isNaN(t)) return false;
    return Date.now() - t < SUBMIT_MAX_AGE_MS;
  }

  // Liquid rendered the cart size into the wrapper; /cart.js confirms it, because
  // that is the value an intermediary cache cannot have made stale.
  function cartIsEmpty(done) {
    var rendered = parseInt(root.getAttribute('data-cart-count') || '', 10);
    if (!isNaN(rendered) && rendered > 0) {
      done(false);
      return;
    }
    if (!window.fetch) {
      done(false); // cannot confirm -> do not claim anything
      return;
    }
    window
      .fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root + 'cart.js'
        : '/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (cart) {
        done(!!cart && cart.item_count === 0);
      })
      .catch(function () {
        done(false);
      });
  }

  function decide(done) {
    if (designMode) {
      done(true);
      return;
    }
    if (urlAsksForIt()) {
      done(true);
      return;
    }
    if (dismissed()) {
      done(false);
      return;
    }
    if (!submittedRecently() || !cameBackFromThankYou()) {
      done(false);
      return;
    }
    cartIsEmpty(done);
  }

  /* ------------------------------------------------------------------ *
   * Stand down for anything that outranks a confirmation                *
   * ------------------------------------------------------------------ */

  function reallyVisible(el) {
    if (!el || el.hasAttribute('hidden')) return false;
    var cs = window.getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') return false;
    var r = el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return false;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var vw = window.innerWidth || document.documentElement.clientWidth;
    if (r.bottom <= 0 || r.top >= vh || r.right <= 0 || r.left >= vw) return false;
    return true;
  }

  // Shopify's native consent UI, matched by id and by class, then filtered down
  // to elements actually painted and actually intersecting the viewport - so a
  // banner parked off-screen after dismissal can never hold this panel back.
  function consentUiVisible() {
    var nodes = document.querySelectorAll(CONSENT_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      if (reallyVisible(nodes[i])) return true;
    }
    return false;
  }

  // The launch welcome dialog sits at z-index 1000000, above this panel. Two
  // notices stacked on one screen is worse than either alone, so wait it out.
  function welcomeDialogOpen() {
    var w = document.getElementById('hp-welcome');
    return !!w && !w.hasAttribute('hidden');
  }

  // It is on the page but already dismissed this session, or it is not on the
  // page at all (switched off in theme settings) - either way it cannot appear.
  function welcomeCanStillOpen() {
    if (!document.getElementById('hp-welcome')) return false;
    return readSession(WELCOME_KEY) !== '1';
  }

  function blocked() {
    return consentUiVisible() || welcomeDialogOpen();
  }

  // The welcome dialog's clock does not start at page load - it starts when the
  // consent banner clears. So the wait has to be measured from the moment THIS
  // screen became clear, not from page load, or a banner answered 30 s in would
  // reopen exactly the same race.
  function readyToOpen() {
    var now = Date.now();
    if (now - armedAt < SHOW_DELAY_MS) return false;
    if (welcomeCanStillOpen() && now - clearSince < WELCOME_WORST_CASE_MS) return false;
    return true;
  }

  // One watcher for the whole lifetime of the page. It opens the panel when the
  // screen is clear, and stands it down again - without recording a dismissal -
  // if a consent banner or the welcome dialog turns up afterwards. Nothing here
  // depends on winning a race against another component's timers.
  function startGuard() {
    armedAt = Date.now();
    clearSince = blocked() ? 0 : armedAt;
    if (designMode) {
      open();
      return;
    }
    guard = window.setInterval(function () {
      if (userDismissed) {
        window.clearInterval(guard);
        guard = null;
        return;
      }
      if (blocked()) {
        clearSince = 0;
        if (isOpen) standDown();
        return;
      }
      if (!clearSince) clearSince = Date.now();
      if (!isOpen && readyToOpen()) open();
    }, POLL_MS);
  }

  /* ------------------------------------------------------------------ *
   * Scroll affordance - only when there is genuinely more to read       *
   * ------------------------------------------------------------------ */

  function syncScrollState() {
    if (!scroller) return;
    var hidden = scroller.scrollHeight - scroller.clientHeight;
    root.classList.toggle('hp-qack--more', hidden > 4 && scroller.scrollTop < hidden - 4);
    root.classList.toggle('hp-qack--scrolled', scroller.scrollTop > 2);
  }

  function watchScroll() {
    if (!scroller) return;
    syncScrollState();
    scroller.addEventListener('scroll', syncScrollState, { passive: true });
    window.addEventListener('resize', syncScrollState);
    if (window.ResizeObserver) {
      scrollObserver = new window.ResizeObserver(syncScrollState);
      scrollObserver.observe(scroller);
      for (var i = 0; i < scroller.children.length; i++) scrollObserver.observe(scroller.children[i]);
    }
    // Oswald/Archivo land late and change how much there is to read.
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(syncScrollState).catch(function () {});
    }
  }

  /* ------------------------------------------------------------------ *
   * Open / close. No scroll lock, no focus trap - this is not a modal.  *
   * ------------------------------------------------------------------ */

  function open() {
    if (isOpen) return;
    if (!designMode && blocked()) return; // last guard before painting
    isOpen = true;

    lastFocused = document.activeElement;
    root.hidden = false;
    document.documentElement.classList.add(HTML_OPEN_CLASS);

    // Force a frame so the transition runs from the hidden state.
    void root.offsetHeight;
    root.classList.add('is-open');

    if (scroller) scroller.scrollTop = 0;
    watchScroll();

    // One deliberate focus move: the panel is why the buyer is on this page.
    // Focus is NOT trapped afterwards - Tab and Shift+Tab leave it freely.
    try {
      panel.focus({ preventScroll: true });
    } catch (e) {
      panel.focus();
    }

    document.addEventListener('keydown', onKeydown);
  }

  // Get out of the way for something that outranks a confirmation. Deliberately
  // NOT a dismissal: no flag is written, no focus is moved, and the guard puts
  // the panel back as soon as the screen is clear again.
  function standDown() {
    if (!isOpen) return;
    isOpen = false;
    document.documentElement.classList.remove(HTML_OPEN_CLASS);
    root.classList.remove('is-open');
    document.removeEventListener('keydown', onKeydown);
    if (scrollObserver) {
      scrollObserver.disconnect();
      scrollObserver = null;
    }
    window.setTimeout(function () {
      if (isOpen) return;
      root.hidden = true;
      root.classList.remove('hp-qack--more', 'hp-qack--scrolled');
    }, CLOSE_ANIM_MS);
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    userDismissed = true;

    document.documentElement.classList.remove(HTML_OPEN_CLASS);
    root.classList.remove('is-open');
    document.removeEventListener('keydown', onKeydown);
    writeSession(DISMISS_KEY, '1');

    // Take the marker out of the address bar so a reload or a shared link does
    // not reopen a confirmation the buyer has already read.
    try {
      if (urlAsksForIt() && window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.delete('hp-quote');
        url.searchParams.delete('hp_quote');
        window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash);
      }
    } catch (e) {
      /* address bar untouched - harmless */
    }

    if (scrollObserver) {
      scrollObserver.disconnect();
      scrollObserver = null;
    }

    window.setTimeout(function () {
      if (isOpen) return;
      root.hidden = true;
      root.classList.remove('hp-qack--more', 'hp-qack--scrolled');
    }, CLOSE_ANIM_MS);

    // Give focus back where it was. On a fresh page load that is <body>, whose
    // focus() is a no-op, so hand it to <main> instead rather than leaving focus
    // on a control that is about to be hidden.
    var target = lastFocused;
    if (!target || target === document.body || !document.contains(target)) {
      target = document.getElementById('MainContent') || document.querySelector('main');
      if (target && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    }
    if (target && target.focus) {
      try {
        target.focus({ preventScroll: true });
      } catch (e) {
        target.focus();
      }
    }
    lastFocused = null;
  }

  function onKeydown(event) {
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.stopPropagation();
      close();
    }
  }

  root.addEventListener('click', function (event) {
    var target = event.target;
    if (target && target.closest && target.closest('[data-hp-qack-close]')) {
      event.preventDefault();
      close();
    }
  });

  /* ------------------------------------------------------------------ *
   * Go                                                                  *
   * ------------------------------------------------------------------ */

  function start() {
    decide(function (show) {
      if (!show) return;
      startGuard();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
